import { useMapStore, useUserRolePermissionStore } from 'contexts/RootStore.context';
import { Horizontal } from 'gls';
import MultiSelectCheckbox from 'components/inputs/Select/MultiSelectCheckbox';
import { observer } from 'mobx-react';
import { style } from 'typestyle';
import { headerStyle } from 'styling/App.style';
import CategorySearchSelect from 'components/inputs/Select/CategorySearchSelect/CategorySearchSelect';
import useMap from 'views/map/hooks/useMap';
import { MapContainerRefContext } from 'views/map/utils/mapContainerRef.context';
import { useContext, useMemo } from 'react';
import { KeyValuePair } from 'utils/common';
import { SearchInput } from 'components/SearchInput/SearchInput';
import { AssetPermissions } from 'views/config/roles/RoleConfigDto';
import { Device } from 'types/device';
import { User } from 'types/userInfo';
import { Asset } from 'types/asset';

export const FilterForm = observer(() => {

  const {
    filterForm,
    showAssetMarkers,
    assetStatuses,
    loadAssets,
    loadUsers,
    loadDevices,
    selectBeacon,
    abortController,
  } = useMapStore()
  const { getAssetTypes, getAssetOwners, canViewUsers, canViewAssets, canViewDevices } = useUserRolePermissionStore()
  const { map } = useContext(MapContainerRefContext)
  const { fitMapToFilterResults } = useMap(useMapStore())

  const statusOptions: KeyValuePair[] = useMemo(() => {
    const options = assetStatuses.map(r => ({ key: r.description, value: r.asset_status_id }))
    options.push({ key: "No status set", value: "__null" })
    return options;
  }, [assetStatuses])

  const { asset_types, asset_owners } = useMemo(() => {
    return {
      asset_types: getAssetTypes(AssetPermissions.view),
      asset_owners: getAssetOwners(AssetPermissions.view)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onlyViewAssets = canViewAssets && !canViewUsers && !canViewDevices
  const onlyViewUsers = !canViewAssets && canViewUsers && !canViewDevices
  const onlyViewDevices = !canViewAssets && !canViewUsers && canViewDevices

  const searchCategoriesOptions = useMemo(() => {
    const lst = []
    if (canViewAssets && filterForm.$.show.value.includes("assets"))
      lst.push({ key: "Asset Name", value: "assets" })
    if (canViewUsers && filterForm.$.show.value.includes("users"))
      lst.push({ key: "Persons Name", value: "users" })
    if (canViewDevices && filterForm.$.show.value.includes("devices"))
      lst.push({ key: "Device Id", value: "devices" })
    return lst
  }, [canViewAssets, canViewDevices, canViewUsers, filterForm.$.show.value])

  const viewOptions = useMemo(() => {
    const options = []
    if (canViewAssets)
      options.push({ key: "Assets", value: "assets" })
    if (canViewUsers)
      options.push({ key: "People", value: "users" })
    if (canViewDevices)
      options.push({ key: "Devices", value: "devices" })
    return options
  }, [canViewAssets, canViewDevices, canViewUsers])

  const searchTermPresent = filterForm.$.searchTerm.value !== ""; // note we hide dropdowns when there is a searchterm

  const onSearch = async (searchTerm: string, searchCategory: string) => {
    filterForm.$.searchTerm.onChange(searchTerm)
    filterForm.$.searchIn.onChange(searchCategory)
    if (searchCategory === "") {
      canViewAssets && loadAssets()
      canViewUsers && loadUsers()
      canViewDevices && loadDevices()
      return
    }

    let items: Asset[] | User[] | Device[] = []
    switch (searchCategory) {
      case "assets":
        items = await loadAssets()
        break
      case "users":
        items = await loadUsers()
        break
      case "devices":
        items = await loadDevices()
        break
    }

    if (abortController.signal.aborted)
      return

    selectBeacon(null)
    if (items.length === 0)
      return `Sorry, no ${searchCategory === "assets" ? "assets" : searchCategory === "users" ? "people" : "devices"} matched ${searchTerm}`

    map && fitMapToFilterResults(map, items)
  }

  const filterAssets = () => {
    loadAssets().then(items => {
      map && fitMapToFilterResults(map, items)
    })
  }

  const onChangeShow = (values: any[]) => {
    if (values.length === 0)
      values = filterForm.$.show.value.includes("assets") ? ["users"] : ["assets"]
    if (values.includes("users") && values.includes("devices"))
      values = values.filter(v => v !== (filterForm.$.show.value.includes("users") ? "users" : "devices"))
    filterForm.$.show.onChange(values)
  }

  return (
    <>
      <Horizontal className={headerStyle} spacing={10}>
        {searchCategoriesOptions.length > 1 && (
          <CategorySearchSelect
            disableSearchOnTextOnly
            placeholder={`Search ${searchCategoriesOptions.map(r => r.value).join("/")}`}
            searchCategoriesList={searchCategoriesOptions}
            onSearch={onSearch}
            minCharForSearch={1}
            onInputchange={filterForm.$.searchTerm.onChange}
          />
        )}
        {onlyViewAssets && (
          <SearchInput
            width="350px"
            id='search-asset'
            value={filterForm.$.searchTerm.value}
            onChange={filterForm.$.searchTerm.onChange}
            onEnterSearch={(value) => {
              const SearchIn = value !== "" ? "assets" : ""
              filterForm.$.searchIn.onChange(SearchIn)
              filterForm.$.searchTerm.onChange(value)
              return onSearch(value, SearchIn)
            }}
          />
        )}
        {onlyViewUsers && (
          <SearchInput
            width="350px"
            id='search-user'
            value={filterForm.$.searchTerm.value}
            onChange={filterForm.$.searchTerm.onChange}
            onEnterSearch={(value) => {
              const SearchIn = value !== "" ? "users" : ""
              filterForm.$.searchIn.onChange(SearchIn)
              filterForm.$.searchTerm.onChange(value)
              return onSearch(value, SearchIn)
            }}
          />
        )}
        {onlyViewDevices && (
          <SearchInput
            width="350px"
            id='search-device'
            value={filterForm.$.searchTerm.value}
            onChange={filterForm.$.searchTerm.onChange}
            onEnterSearch={(value) => {
              const SearchIn = value !== "" ? "devices" : ""
              filterForm.$.searchIn.onChange(SearchIn)
              filterForm.$.searchTerm.onChange(value)
              return onSearch(value, SearchIn)
            }}
          />
        )}

        {viewOptions.length >= 2 && !searchTermPresent && (
          <MultiSelectCheckbox
            id="assets-people-select"
            includeAll={false}
            isClearable={false}
            options={viewOptions}
            value={filterForm.$.show.value}
            onChange={onChangeShow}
            height='40px'
            width='340px'
            selectedValuePrefix='Show: '
            selectedTextRender={(values) => values.map(r => r.label).filter(r => r !== "All").join(' & ')}
          />
        )}
      </Horizontal >
      {showAssetMarkers && canViewAssets && !searchTermPresent && (
        <Horizontal className={headerStyle} spacing={10}>
          <MultiSelectCheckbox
            id='asset-types-select'
            includeAll
            isClearable={false}
            selectedValuePrefix='Asset Type: '
            options={asset_types.map(r => ({ key: r.label, value: r.value }))}
            value={filterForm.$.assetTypes.value}
            onChange={(values) => {
              filterForm.$.assetTypes.onChange(values)
              filterAssets()
            }}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            placeholder='Asset Type: (0)'
            className={filterComponentStyle}
            height='40px'
          />
          <MultiSelectCheckbox
            id='owners-select'
            includeAll
            isClearable={false}
            selectedValuePrefix='Owners: '
            placeholder='Owners: (0)'
            options={asset_owners.map(r => ({ key: r.label, value: r.value }))}
            value={filterForm.$.owners.value}
            onChange={(values) => {
              filterForm.$.owners.onChange(values)
              filterAssets()
            }}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            className={filterComponentStyle}
            height='40px'
          />
          <MultiSelectCheckbox
            id='asset-statuses-select'
            includeAll
            isClearable={false}
            selectedValuePrefix='Status: '
            options={statusOptions}
            value={filterForm.$.assetStatuses.value}
            onChange={(values) => {
              filterForm.$.assetStatuses.onChange(values)
              filterAssets()
            }}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            placeholder='Status: (0)'
            className={filterComponentStyle}
            height='40px'
          />
        </Horizontal>
      )
      }
    </>
  );
})

const filterComponentStyle = style({
  minWidth: '160px',
  maxWidth: '300px',
})
